import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { Gender, UnitSystem } from "@/Common/Models/Patient/PatientEnums";
import { AgeUnit, ChartId, VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { AxisConfiguration } from "@/Patient/Models/GrowthCharts/ChartData";
import { CurvesName } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { AxisLine } from "../../Charts/GrowthChartGraph";
import { GrowthChartModel } from "./GrowthChartModel";

abstract class Age0To5GrowthChart extends GrowthChartModel {
    public isDisplayedByDefault(): boolean {
        return (LuxonDateHelper.dateDiff(this.patient.DateOfBirth, DateTime.nowDate(), "years") <= 5 && !this.getCorrectedAge())
            || (this.getCorrectedAge() >= 0 && this.getCorrectedAge() <= 5);
    }

    public isCompatible(): boolean {
        return super.isCompatible() && this.vitalSigns
            .filter(vital => !!vital[this.vitalProperty])
            .some(vital => this.filterVitalSign(vital));
    }

    public filterVitalSign(vitalSign: MedicalItemVitalSign): boolean {
        if (!this.patient.DateOfBirth)
            return false;

        return LuxonDateHelper.dateDiff(this.patient.DateOfBirth, vitalSign.PharmaStartDate, "months") <= 60;
    }

    public get isVitalSign(): boolean {
        return false;
    }

    public get ageUnit(): AgeUnit {
        return "month";
    }
}

export class Age0To5HeadCircumference extends Age0To5GrowthChart {
    public get curveName(): CurvesName {
        return this.gender === Gender.Male ? "headCircMale0-5" : "headCircFemale0-5";
    }

    public get id(): ChartId {
        return "HCFA05";
    }

    public get name(): string {
        return cacheStore.GetString("Age0To5HeadCircumferenceLabel");
    }

    public get shortName(): string {
        return cacheStore.GetString("Age0To5HeadCircumferenceShortLabel");
    }

    public get hideXAxisGrid(): boolean {
        return true;
    }

    public get xAxis(): AxisConfiguration {
        return {
            label: `${cacheStore.GetString("AgeLabel")} (${cacheStore.GetString("MonthAbbrevLabel")})`,
            min: 0,
            max: 60,
            interval: 2
        };
    }

    public get yAxis(): AxisConfiguration {
        return this.unit === UnitSystem.Metric ?
            {
                label: `${cacheStore.GetString("HeadCircumferenceLabel")} (${cacheStore.GetString("CentimeterUnitSymbolLabel")})`,
                min: 32,
                max: 54,
                interval: 2
            } :
            {
                label: `${cacheStore.GetString("HeadCircumferenceLabel")} (${cacheStore.GetString("InchUnitSymbolLabel")})`,
                min: 12,
                max: 22,
                interval: 1
            };
    }

    public get vitalProperty(): VitalProperty {
        return "HeadCircumference";
    }

    public getLines(): AxisLine[] {
        const lines = super.getLines();
        const gridStep = 4;
        const gridLimit = Math.floor(this.xAxis.max / gridStep);

        for (let i = 1; i <= gridLimit; i++) {
            lines.push({
                label: null,
                value: gridStep * i,
                className: "c3-xgrid"
            });
        }

        return lines;
    }
}
